import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { TranslationService } from 'src/app/i18n/translation.service';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { ScreenSupportService, SupportSession } from '../screen-support.service';
import { CallWindowComponent } from 'src/app/modules/commons/components/call-window/call-window.component';

/**
 * The call window of a screen support session, for the customer and for the support agent. Opening it joins the room with a
 * fresh one-time link (the server checks every gate again); closing it with X only leaves the room, the session stays open
 * until somebody stops it, it expires, or the master switch is turned off.
 *
 * The customer always presses the share button inside the call themselves and picks a tab or window: no page can start a
 * screen share silently, and this one does not try. Phones and the Android / iPhone app cannot share a screen from web code,
 * so the customer is told to use a computer browser (they can still talk and chat).
 */
@Component({
  selector: 'app-screen-support-room',
  standalone: true,
  imports: [CommonModule, SHARED_MODULES, CallWindowComponent],
  templateUrl: './screen-support-room.component.html',
  styleUrls: ['./screen-support-room.component.css'],
})
export class ScreenSupportRoomComponent implements OnChanges {

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() mode: 'customer' | 'staff' = 'customer';
  @Input() session: SupportSession | null = null;
  /** The session changed (it was stopped from this window): the parent re-reads. */
  @Output() changed = new EventEmitter<void>();

  state: 'idle' | 'joining' | 'live' | 'error' = 'idle';
  errorText = '';
  frameUrl: SafeResourceUrl | null = null;
  joinUrl = '';
  stopping = false;
  /** Customer: the "how to share" card over the call. Open when the call starts; closed with its X, reopened from the top bar. */
  helpOpen = true;

  /** A computer browser can share a screen; a phone or the app cannot. */
  readonly canShare = !Capacitor.isNativePlatform() && !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia);

  constructor(
    private api: ScreenSupportService,
    private sanitizer: DomSanitizer,
    private i18n: TranslationService,
    private toast: ToastMessagesService,
  ) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['visible'] && this.visible) this.join();
  }

  private t(key: string): string { return this.i18n.instant(key); }

  /** The thin top bar's second line: the customer, company and ticket for support; the privacy line for the customer. */
  get subtitle(): string {
    const s = this.session;
    if (this.mode === 'customer') return this.t('ss.liveBanner');
    if (!s) return '';
    return [s.customerName || s.customerEmail, s.companyName, s.ticketNumber].filter((x) => !!x).join(' · ');
  }

  private join(): void {
    if (!this.session) return;
    this.errorText = '';
    this.state = 'joining';
    const call = this.mode === 'customer' ? this.api.join(this.session.id) : this.api.staffJoin(this.session.id);
    call.subscribe({
      next: (r) => {
        this.joinUrl = r.joinUrl;
        this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(r.joinUrl);
        this.state = 'live';
        this.helpOpen = true;
        this.changed.emit();
      },
      error: (e) => {
        this.errorText = e?.error?.message || this.t('ss.failed');
        this.state = 'error';
      },
    });
  }

  retry(): void { this.join(); }

  /** The window closed: leave the room (its one-time link is used up). The session itself stays open. */
  onHide(): void {
    if (!this.visible) return;
    this.frameUrl = null;
    this.state = 'idle';
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /** A join link works for only 10 minutes: ask for a fresh one on every press, and open the tab first (same tap) so it is not blocked. */
  opening = false;
  openInTab(): void {
    if (!this.session || this.opening) return;
    this.opening = true;
    const native = Capacitor.isNativePlatform();
    const tab = native ? null : window.open('', '_blank');
    const call = this.mode === 'customer' ? this.api.join(this.session.id) : this.api.staffJoin(this.session.id);
    call.subscribe({
      next: (r) => {
        this.opening = false;
        this.joinUrl = r.joinUrl;
        if (native) Browser.open({ url: r.joinUrl }).catch(() => {});
        else if (tab) { try { tab.opener = null; } catch { /* ignore */ } tab.location.href = r.joinUrl; }
        else window.location.assign(r.joinUrl);
      },
      error: (e) => {
        this.opening = false;
        try { tab?.close(); } catch { /* ignore */ }
        this.errorText = e?.error?.message || this.t('ss.failed');
        this.state = 'error';
      },
    });
  }

  stop(): void {
    if (!this.session || this.stopping) return;
    if (!confirm(this.t('ss.stopConfirm'))) return;
    this.stopping = true;
    const call = this.mode === 'customer' ? this.api.end(this.session.id) : this.api.staffEnd(this.session.id);
    call.subscribe({
      next: () => {
        this.stopping = false;
        this.changed.emit();
        this.onHide();
      },
      error: (e) => {
        this.stopping = false;
        this.toast.showErrorMessage(this.t('ss.title'), e?.error?.message || this.t('ss.failed'));
      },
    });
  }
}
